import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import ReactMarkdown from 'react-markdown';
import api from '../../api';
import {
  ProDocItem as ProDocItemType,
  UpdateProDocCheckboxRequest,
  UpdateProDocCheckboxResponse,
  GetConfirmationsBatchResponse,
  UIDSLComponent,
} from '@dreamai/types';
import { ReactComponent as ExternalLinkIcon } from '../../icons/external-link.svg';
import { UIUpdate } from './canvas/UIComponentRenderer';

interface ProDocItemProps {
  item: ProDocItemType;
  onCheckboxUpdate?: (itemId: string, checked: boolean) => void;
  inComponentView?: boolean;
}

/**
 * Helper function to check if text is surrounded by strikethrough markdown
 */
const isStrikethrough = (text: string | undefined | null): boolean => {
  if (!text) return false;
  const trimmed = text.trim();
  return trimmed.startsWith('~~') && trimmed.endsWith('~~');
};

/**
 * Helper function to remove strikethrough markdown and return clean text
 */
const removeStrikethroughMarkdown = (text: string): string => {
  const trimmed = text.trim();
  if (trimmed.startsWith('~~') && trimmed.endsWith('~~')) {
    return trimmed.slice(2, -2);
  }
  return text;
};

const ProDocItem: React.FC<ProDocItemProps> = ({
  item,
  onCheckboxUpdate,
  inComponentView,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [isUpdating, setIsUpdating] = useState(false);
  const [pendingConfirmationsCount, setPendingConfirmationsCount] = useState<
    number | null
  >(null);

  // Fetch pending confirmations count for ConfirmationCarousel components
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (!item.component || item.component.type !== 'ConfirmationCarousel') {
        return;
      }

      const confirmationIds = item.component.props?.confirmationIds;
      if (
        !confirmationIds ||
        !Array.isArray(confirmationIds) ||
        confirmationIds.length === 0
      ) {
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await api.post<GetConfirmationsBatchResponse>(
          '/confirmation-auth/batch',
          { confirmationIds },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          const pendingCount = response.data.confirmations.filter(
            (c) => c.status === 'PENDING',
          ).length;
          setPendingConfirmationsCount(pendingCount);
        }
      } catch (error) {
        console.error('Error fetching confirmation count:', error);
      }
    };

    fetchPendingCount();
  }, [item.component?.type, item.component?.props, getAccessTokenSilently]);

  const handleCheckboxChange = async () => {
    if (isUpdating) return;

    const newChecked = !item.checked;
    setIsUpdating(true);

    // Optimistically update parent
    if (onCheckboxUpdate) {
      onCheckboxUpdate(item.id, newChecked);
    }

    try {
      const token = await getAccessTokenSilently();

      const response = await api.patch<UpdateProDocCheckboxResponse>(
        `/pro-docs/items/${item.id}/checkbox`,
        { checked: newChecked },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.data.success) {
        // Revert on error
        if (onCheckboxUpdate) {
          onCheckboxUpdate(item.id, item.checked);
        }
        console.error('Failed to update checkbox:', response.data.error);
      }
    } catch (error) {
      // Revert on error
      if (onCheckboxUpdate) {
        onCheckboxUpdate(item.id, item.checked);
      }
      console.error('Error updating checkbox:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const componentType = item.component?.type;
  const isTextComponent = componentType === 'Text';
  const isMarkdownComponent = componentType === 'Markdown';
  const isHeadingComponent = componentType === 'Heading';
  const isConfirmationCarousel = componentType === 'ConfirmationCarousel';

  // Text and Markdown components display their content directly (no caption)
  const isContentOnlyComponent = isTextComponent || isMarkdownComponent;

  // Check if text/caption should be strikethrough
  const textValue = item.component?.props?.value as string;
  const markdownContent = item.component?.props?.content as string;
  const headingValue = item.component?.props?.value as string;

  const isTextStrikethrough = isTextComponent && isStrikethrough(textValue);
  const isMarkdownStrikethrough =
    isMarkdownComponent && isStrikethrough(markdownContent);
  const isCaptionStrikethrough =
    !isContentOnlyComponent && isStrikethrough(item.caption);

  // Get clean text without markdown
  const displayText = isTextStrikethrough
    ? removeStrikethroughMarkdown(textValue)
    : textValue;
  const displayMarkdown = isMarkdownStrikethrough
    ? removeStrikethroughMarkdown(markdownContent)
    : markdownContent;
  let displayCaption = isCaptionStrikethrough
    ? removeStrikethroughMarkdown(item.caption || '')
    : item.caption;

  // Convert \n to proper markdown formatting
  const formatTextForMarkdown = (text: string | undefined): string => {
    if (!text) return '';
    // ReactMarkdown needs actual blank lines for paragraph breaks
    // Single \n becomes a line break within a paragraph (needs two spaces before)
    // Double \n\n becomes a paragraph break (needs to stay as is)
    // We need to handle this carefully to preserve intentional paragraph breaks

    // First, protect existing \n\n by replacing with a placeholder
    const withPlaceholder = text.replace(/\n\n/g, '<<<PARAGRAPH_BREAK>>>');
    // Then add two spaces before remaining single \n for line breaks
    const withLineBreaks = withPlaceholder.replace(/\n/g, '  \n');
    // Finally, restore paragraph breaks
    return withLineBreaks.replace(/<<<PARAGRAPH_BREAK>>>/g, '\n\n');
  };

  // Interpolate [#]X[/#] for ConfirmationCarousel
  if (isConfirmationCarousel && pendingConfirmationsCount !== null) {
    displayCaption =
      displayCaption?.replace(
        /\[#\].*?\[\/#\]/g,
        String(pendingConfirmationsCount),
      ) || '';
  }

  // Determine if text should be strikethrough (either markdown or checked)
  // For ConfirmationCarousel, also strikethrough if pending count is 0
  const shouldStrikethrough =
    isTextStrikethrough ||
    isMarkdownStrikethrough ||
    isCaptionStrikethrough ||
    item.checked ||
    (isConfirmationCarousel && pendingConfirmationsCount === 0);

  // Render heading component directly
  if (isHeadingComponent) {
    let level = item.component?.props?.level;

    // Normalize level: handle both "1" and "h1" formats, and numbers
    if (typeof level === 'string' && level.startsWith('h')) {
      level = level.substring(1);
    } else if (typeof level === 'number') {
      level = String(level);
    } else if (level === undefined || level === null) {
      level = '6'; // Default to h1
    }

    const headingStyle = shouldStrikethrough
      ? { textDecoration: 'line-through' as const }
      : undefined;

    return (
      <div className='pro-doc-item'>
        {item.checkbox && (
          <div className='checkbox-wrapper'>
            <input
              type='checkbox'
              checked={item.checked}
              onChange={handleCheckboxChange}
              disabled={isUpdating}
            />
          </div>
        )}
        {level === '1' && <h1 style={headingStyle}>{headingValue}</h1>}
        {level === '2' && <h2 style={headingStyle}>{headingValue}</h2>}
        {level === '3' && <h3 style={headingStyle}>{headingValue}</h3>}
        {level === '4' && <h4 style={headingStyle}>{headingValue}</h4>}
        {level === '5' && <h5 style={headingStyle}>{headingValue}</h5>}
        {level === '6' && <h6 style={headingStyle}>{headingValue}</h6>}
      </div>
    );
  }

  return (
    <>
      <div className='pro-doc-item'>
        {item.checkbox && (
          <div className='checkbox-wrapper'>
            <input
              type='checkbox'
              checked={item.checked}
              onChange={handleCheckboxChange}
              disabled={isUpdating}
            />
          </div>
        )}

        {/* Text component: Display text directly */}
        {isTextComponent && (
          <div>
            <p className='pro-doc-caption'>
              <span
                style={
                  shouldStrikethrough
                    ? { textDecoration: 'line-through' }
                    : undefined
                }
              >
                <ReactMarkdown>
                  {formatTextForMarkdown(displayText)}
                </ReactMarkdown>
              </span>
              {item.external_links && item.external_links?.length > 0 && (
                <span className='external-links'>
                  {item.external_links.map((link, index) => (
                    <a
                      key={index}
                      href={link.link}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {link.text}
                      <ExternalLinkIcon />
                    </a>
                  ))}
                </span>
              )}
            </p>
          </div>
        )}

        {/* Markdown component: Display markdown directly */}
        {isMarkdownComponent && (
          <div>
            <p className='pro-doc-caption'>
              <span
                style={
                  shouldStrikethrough
                    ? { textDecoration: 'line-through' }
                    : undefined
                }
              >
                <ReactMarkdown>
                  {formatTextForMarkdown(displayMarkdown)}
                </ReactMarkdown>
              </span>
              {item.external_links && item.external_links?.length > 0 && (
                <span className='external-links'>
                  {item.external_links.map((link, index) => (
                    <a
                      key={index}
                      href={link.link}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {link.text}
                      <ExternalLinkIcon />
                    </a>
                  ))}
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Render component inline for non-Text, non-Markdown, non-Heading components */}
      {!isContentOnlyComponent && !isHeadingComponent && item.component && (
        <div className='pro-doc-component-inline'>
          <UIUpdate
            component={
              {
                id: `prodoc-component-${item.id}`,
                type: item.component.type,
                props: item.component.props,
              } as UIDSLComponent
            }
            viewId={`prodoc-item-${item.id}`}
            threadId={null}
            section='prodoc'
            onUserInput={() => {}}
            updateThreadViewVisualDescription={async () => {}}
          />
        </div>
      )}

      {/* All other components: Display caption below component */}
      {!isContentOnlyComponent && !isHeadingComponent && item.caption && (
        <p className='pro-doc-caption true-below-caption'>
          <span
            style={
              shouldStrikethrough
                ? { textDecoration: 'line-through' }
                : undefined
            }
          >
            <ReactMarkdown>
              {formatTextForMarkdown(displayCaption)}
            </ReactMarkdown>
          </span>{' '}
          {item.external_links && item.external_links?.length > 0 && (
            <span className='external-links'>
              {item.external_links.map((link, index) => (
                <a
                  key={index}
                  href={link.link}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {link.text}
                  <ExternalLinkIcon />
                </a>
              ))}
            </span>
          )}
        </p>
      )}
    </>
  );
};

export default ProDocItem;
